import { useMutation, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@/api/client";
import type { components } from "@/api/schema";

import { stockKeys } from "./query-keys";

type Marketplace = components["parameters"]["Marketplace"];

interface BulkUpdateVars {
  rows: { sku: string; mkp: string }[];
  body: { multiplier?: string; fake?: number; lock?: number };
}

export function useBulkUpdateStock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ rows, body }: BulkUpdateVars) => {
      // The backend accepts exactly one of multiplier/fake/lock per
      // PATCH, so a multi-field bulk edit must fan out into one request per
      // field per row.
      const fieldBodies: BulkUpdateVars["body"][] = [];
      if (body.multiplier !== undefined) fieldBodies.push({ multiplier: body.multiplier });
      if (body.fake !== undefined) fieldBodies.push({ fake: body.fake });
      if (body.lock !== undefined) fieldBodies.push({ lock: body.lock });

      const requests = rows.flatMap(({ sku, mkp }) =>
        fieldBodies.map((singleBody) =>
          apiClient.PATCH("/v1/stock/{sku}/{mkp}", {
            params: { path: { sku, mkp: mkp as Marketplace } },
            body: singleBody,
          }),
        ),
      );
      const results = await Promise.all(requests);
      const failed = results.filter((r) => r.error).length;
      if (failed > 0) {
        throw new Error(`${failed} of ${requests.length} updates failed`);
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: stockKeys.lists });
    },
  });
}
